import type { ActivityItem, LaunchSummary } from "@bubblefun/shared";
import { relativeTime, shortAddress, tradeEthAmount } from "../lib/format";
import { Link } from "../lib/router";

interface TickerEvent {
  id: string;
  token: `0x${string}`;
  symbol: string;
  side: "buy" | "sell" | "launch";
  amountEth: number | null;
  actor: `0x${string}`;
  timestamp: string;
}

export function LiveActivityTicker({ activity, launches }: { activity: ActivityItem[]; launches: LaunchSummary[] }) {
  const indexed = activity
    .filter((item): item is ActivityItem & { side: "buy" | "sell" | "launch" } => item.side === "buy" || item.side === "sell" || item.side === "launch")
    .map<TickerEvent>((item) => ({
      id: item.id,
      token: item.token,
      symbol: item.symbol,
      side: item.side,
      amountEth: item.side === "launch" ? null : item.amountEth,
      actor: item.trader,
      timestamp: item.timestamp
    }));

  const launchEvents = launches
    .filter((launch) => !indexed.some((item) => item.side === "launch" && item.token.toLowerCase() === launch.address.toLowerCase()))
    .map<TickerEvent>((launch) => ({
      id: `launch-${launch.address}`,
      token: launch.address,
      symbol: launch.symbol,
      side: "launch",
      amountEth: null,
      actor: launch.creator,
      timestamp: launch.createdAt
    }));

  const events = [...indexed, ...launchEvents]
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    .slice(0, 12);

  return (
    <div className="market-ticker" aria-label="Live buys, sells, and token launches">
      <span className="market-ticker-label"><i /> Live</span>
      <div className="market-ticker-viewport">
        <div className="market-ticker-track">
          {[0, 1].map((cycle) => (
            <div className="market-ticker-sequence" key={cycle} aria-hidden={cycle === 1 ? true : undefined}>
              {events.map((event) => (
                <Link className="ticker-event" href={`/token/${event.token}`} key={`${cycle}-${event.id}`}>
                  <strong className={`ticker-side ticker-side--${event.side}`}>{event.side === "launch" ? "Launched" : event.side}</strong>
                  <b>${event.symbol}</b>
                  {event.amountEth !== null && <em>{tradeEthAmount(event.amountEth)}</em>}
                  <small className="ticker-maker">{event.side === "launch" ? "by" : "maker"} {shortAddress(event.actor)}</small>
                  <time dateTime={event.timestamp}>{relativeTime(event.timestamp)}</time>
                </Link>
              ))}
              {!events.length && <span className="ticker-empty">Waiting for the first indexed activity.</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
